import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

/** The Monday Top-10 board email (Brett, 2026-09-21), built verbatim
 *  from planner data. GET renders it as of today; send emails it (to
 *  the caller by default) and snapshots the Top 10 for next week's diff. */
export interface WeeklyUpdate {
  data: {
    as_of: string;
    year: number;
    since: string | null;
    headline: {
      top10: number; green: number; yellow: number; red: number; not_assessed: number;
      go_lives_30: string[]; annual_benefit: number; in_year_benefit: number; cost_avoidance_annual: number; stale: number;
    };
    /** PMO-written intro and standing "You should know" (Markdown). */
    editorial: { intro: string; you_should_know: string };
    /** Pre-send checks (Jim K's 2026-10-19 notes). Page only — never in the email. */
    checks: Array<{ project_id: string; kind: string; text: string }>;
    changes: string[];
    projects: Array<{ id: string; name: string; health: string | null; pulse_stale: boolean }>;
    also_in_flight: Array<{ id: string; name: string }>;
  };
  subject: string;
  html: string;
  text: string;
}

export function useWeeklyUpdate() {
  return useQuery<WeeklyUpdate>({
    queryKey: ["weekly-update"],
    queryFn: () => api.get("/weekly-update/").then((r) => r.data),
    staleTime: 30_000,
  });
}

export function useSendWeeklyUpdate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { to?: string[]; take_snapshot?: boolean; waive_checks?: boolean }) =>
      api.post("/weekly-update/send", body).then((r) => r.data as { sent: boolean; to: string[]; snapshot_taken: boolean; subject: string }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["weekly-update"] }),
  });
}

export interface WeeklyUpdateRecipient {
  email: string;
  display_name: string | null;
}

/** Active planner users for the send-to picker. Anyone else can still
 *  be typed in — board members needn't have a login. */
export function useWeeklyUpdateRecipients(enabled = true) {
  return useQuery<WeeklyUpdateRecipient[]>({
    queryKey: ["weekly-update", "recipients"],
    queryFn: () => api.get("/weekly-update/recipients").then((r) => r.data),
    staleTime: 5 * 60_000,
    enabled,
  });
}

/** Save the intro / "You should know" text (PMO). */
export function useSaveEditorial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { intro: string; you_should_know: string }) =>
      api.put("/weekly-update/editorial", body).then((r) => r.data as { intro: string; you_should_know: string }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["weekly-update"] }),
  });
}
